import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button, Modal, Input, Select } from "@/components/common";
import { Check, ShieldCheck, Users, HeartPulse, Euro, CalendarCheck, ArrowRight, Building2, FileText } from "lucide-react";

const BADGES = { standard: "Più Popolare", professional: "Pro", enterprise: "Custom" };

const FEATURES = [
  { icon: Users, t: "Anagrafica multi-ruolo", d: "Una persona può essere socio, atleta, genitore e istruttore insieme." },
  { icon: HeartPulse, t: "Certificati medici", d: "Semaforo scadenze e promemoria automatici via email." },
  { icon: CalendarCheck, t: "Presenze", d: "Registro presente/assente/giustificato/infortunato con statistiche." },
  { icon: Euro, t: "Quote e pagamenti", d: "Rate, insoluti, crediti da incassare e pagamenti online PayPal." },
  { icon: FileText, t: "Documenti", d: "Fascicolo digitale dell'ASD con cartelle e scadenze." },
  { icon: ShieldCheck, t: "Multi-tenant sicuro", d: "Ogni ASD ha un ambiente completamente separato." },
];

export default function Landing() {
  const { user, registerAsd } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState("standard");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ asd_name: "", tipo: "ASD", codice_fiscale: "", partita_iva: "", admin_name: "", admin_email: "", pin: "", agreement_accepted: false });

  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openReg = (p) => { setPlan(p); setStep(1); setOpen(true); };
  const curPlan = plans.find((p) => p.id === plan);

  const submit = async () => {
    if (!/^[A-Za-z0-9]{8}$/.test(form.pin)) { toast.error("Il PIN deve essere 8 caratteri alfanumerici"); return; }
    if (!form.agreement_accepted) { toast.error("Devi sottoscrivere l'accordo commerciale"); return; }
    setBusy(true);
    try { await registerAsd({ ...form, plan }); toast.success("ASD registrata! 15 giorni di prova gratuita attivati."); navigate("/app"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5"><div className="w-9 h-9 rounded-xl bg-slate-900 grid place-items-center text-white font-bold font-display">A</div><span className="font-bold font-display text-lg text-slate-900">ASD Manager Cloud</span></div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="nav-login-btn">Accedi</Button></Link>
            <Button size="sm" onClick={() => openReg("standard")} data-testid="nav-register-btn">Registra ASD</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100 mb-5">
              <Check size={14} /> 15 giorni di prova gratuita · nessuna carta richiesta
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display tracking-tight text-slate-900 leading-[1.05]">
              Il gestionale cloud<br /><span className="text-sky-600">semplice</span> per ASD/SSD.
            </h1>
            <p className="text-lg text-slate-600 mt-5 max-w-lg">Soci, tesserati, corsi, presenze, certificati medici, quote e contabilità. Un'unica piattaforma con utenti e permessi diversi — un ambiente separato per ogni ASD.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" onClick={() => openReg("standard")} data-testid="hero-register-btn">Inizia la prova gratuita <ArrowRight size={18} /></Button>
              <Link to="/login"><Button size="lg" variant="outline" data-testid="hero-demo-btn">Prova la demo</Button></Link>
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <img src="https://images.unsplash.com/photo-1773232616494-bdd63e307e61?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Impianto sportivo" className="w-full h-[380px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-slate-200 p-4 hidden sm:block">
              <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center"><HeartPulse className="text-emerald-600" size={20} /></div><div><p className="text-xs text-slate-400">Certificati validi</p><p className="font-bold font-display text-slate-900">84 su 103</p></div></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-y border-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-display text-slate-900 text-center">Tutto il necessario, senza complicazioni</h2>
          <p className="text-slate-500 text-center mt-2 max-w-xl mx-auto">Progettato per gestire migliaia di ASD contemporaneamente.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f) => (
              <div key={f.t} className="bg-white border border-slate-200/80 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-slate-900 grid place-items-center mb-4"><f.icon className="text-white" size={20} /></div>
                <h3 className="font-semibold font-display text-slate-800 text-lg">{f.t}</h3>
                <p className="text-sm text-slate-500 mt-1.5">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-display text-slate-900 text-center">Piani su misura per la tua ASD</h2>
          <p className="text-slate-500 text-center mt-2">Tutti i piani includono <strong>15 giorni di prova gratuita</strong>.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {plans.map((p) => {
              const badge = BADGES[p.id];
              return (
                <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative rounded-2xl border p-6 flex flex-col ${badge === "Più Popolare" ? "border-sky-400 shadow-lg ring-1 ring-sky-100" : "border-slate-200"}`}>
                  {badge && <span className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold ${badge === "Più Popolare" ? "bg-sky-600 text-white" : "bg-slate-900 text-white"}`}>{badge}</span>}
                  <h3 className="font-bold font-display text-lg text-slate-900">{p.name}</h3>
                  <div className="mt-2 mb-1">
                    {p.price != null ? <><span className="text-3xl font-bold font-display text-slate-900">€{p.price}</span><span className="text-slate-400 text-sm">/mese</span></> : <span className="text-2xl font-bold font-display text-slate-900">Su misura</span>}
                  </div>
                  <p className="text-xs text-slate-500 mb-4">Fino a {p.max_tesserati >= 1000000 ? "∞" : p.max_tesserati} tesserati</p>
                  <ul className="space-y-2 flex-1 mb-5">{(p.features || []).map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}</ul>
                  <Button variant={badge === "Più Popolare" ? "accent" : "outline"} className="w-full" onClick={() => openReg(p.id)} data-testid={`plan-select-${p.id}`}>{p.id === "enterprise" ? "Contattaci" : "Inizia gratis"}</Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-white"><div className="w-8 h-8 rounded-lg bg-sky-500 grid place-items-center font-bold font-display">A</div><span className="font-bold font-display">ASD Manager Cloud</span></div>
          <p className="text-sm">© 2026 · Piattaforma SaaS per associazioni sportive dilettantistiche</p>
        </div>
      </footer>

      <Modal open={open} onClose={() => setOpen(false)} title={`Registra la tua ASD — Piano ${curPlan?.name || ""}`} wide>
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${step >= s ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-400"}`}>{s}</div>
              {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? "bg-sky-600" : "bg-slate-100"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Nome ASD/SSD" data-testid="reg-asd-name" value={form.asd_name} onChange={(e) => upd("asd_name", e.target.value)} placeholder="A.S.D. Olimpia" />
              <Select label="Tipo" data-testid="reg-tipo" value={form.tipo} onChange={(e) => upd("tipo", e.target.value)}><option value="ASD">ASD</option><option value="SSD">SSD</option></Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Codice Fiscale" data-testid="reg-cf" value={form.codice_fiscale} onChange={(e) => upd("codice_fiscale", e.target.value)} placeholder="90012345678" />
              <Input label="Partita IVA (opz.)" value={form.partita_iva} onChange={(e) => upd("partita_iva", e.target.value)} />
            </div>
            <div className="flex justify-end"><Button onClick={() => form.asd_name ? setStep(2) : toast.error("Inserisci il nome ASD")} data-testid="reg-next-1">Avanti <ArrowRight size={16} /></Button></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Nome amministratore" data-testid="reg-admin-name" value={form.admin_name} onChange={(e) => upd("admin_name", e.target.value)} placeholder="Mario Rossi" />
              <Input label="Email" type="email" data-testid="reg-admin-email" value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value)} placeholder="presidente@asd.it" />
            </div>
            <div>
              <Input label="PIN di accesso (8 caratteri alfanumerici)" data-testid="reg-pin" maxLength={8} value={form.pin} onChange={(e) => upd("pin", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="ES: OLIMPIA1" className="font-mono tracking-widest" />
              <p className="text-xs text-slate-400 mt-1.5">Userai email + questo PIN per accedere.</p>
            </div>
            <div className="flex justify-between"><Button variant="ghost" onClick={() => setStep(1)}>Indietro</Button><Button onClick={() => (form.admin_email && form.admin_name) ? setStep(3) : toast.error("Compila nome ed email")} data-testid="reg-next-2">Avanti <ArrowRight size={16} /></Button></div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 max-h-52 overflow-y-auto text-sm text-slate-600">
              <h4 className="font-semibold text-slate-800 mb-2">Accordo commerciale SaaS</h4>
              <p className="mb-2">Con la presente l'Associazione sottoscrive l'abbonamento al servizio <strong>ASD Manager Cloud</strong> secondo il piano <strong>{curPlan?.name}</strong>{curPlan?.price != null ? ` al canone di €${curPlan.price}/mese` : " con condizioni personalizzate"}, con <strong>15 giorni di prova gratuita</strong> iniziali senza addebiti.</p>
              <p className="mb-2">I dati di ogni ASD sono conservati in un ambiente logicamente separato (multi-tenant). Il servizio è fornito nel rispetto del GDPR. L'abbonamento è mensile e disdicibile in qualsiasi momento.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="reg-agreement" checked={form.agreement_accepted} onChange={(e) => upd("agreement_accepted", e.target.checked)} className="mt-1 w-4 h-4 accent-sky-600" />
              <span className="text-sm text-slate-600">Dichiaro di aver letto e accettato l'accordo commerciale e l'informativa privacy.</span>
            </label>
            <div className="flex justify-between"><Button variant="ghost" onClick={() => setStep(2)}>Indietro</Button><Button variant="emerald" onClick={submit} disabled={busy} data-testid="reg-submit">{busy ? "Creazione..." : "Attiva prova gratuita"}</Button></div>
          </div>
        )}
      </Modal>
    </div>
  );
}
